import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useColors } from "../constants/Colors";
import { useAuth } from "../contexts/AuthContext";
import { useTurnstile } from "../hooks/useTurnstile";
import TurnstileWidget from "./TurnstileWidget";
import { usePostHog } from "posthog-js/react";

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  redirectToProfileOnSignup?: boolean;
}

type AuthMode = "signin" | "signup";
type AuthError = Error & { code?: string };

const AuthModal: React.FC<AuthModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  redirectToProfileOnSignup = true,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    containerNeutralSolidSurface,
    textNeutralDefault,
    textNeutralMuted,
    accentColor,
    textNeutralOnPrimary,
  } = useColors();
  const { signInWithGoogle, signInWithApple, signInWithEmail, signUp, user } =
    useAuth();
  const posthog = usePostHog();

  const {
    token: turnstileToken,
    resetToken,
    siteKey,
    widgetRef,
    onSuccess: onTurnstileSuccess,
    onExpire: onTurnstileExpire,
    onError: onTurnstileError,
  } = useTurnstile();

  const [mode, setMode] = useState<AuthMode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (user && isOpen) {
      onSuccess();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isOpen]);

  if (!isOpen) return null;

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError("");
    try {
      posthog?.capture("login_started", { method: "google" });
      await signInWithGoogle();
      posthog?.capture("login_success", { method: "google" });
      onSuccess();
    } catch (err) {
      const authError = err as AuthError;
      posthog?.capture("login_failed", {
        method: "google",
        code: authError.code,
        message: authError.message,
      });
      setError(authError.message || "Failed to sign in with Google");
    } finally {
      setLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    setLoading(true);
    setError("");
    try {
      posthog?.capture("login_started", { method: "apple" });
      await signInWithApple();
      posthog?.capture("login_success", { method: "apple" });
      onSuccess();
    } catch (err) {
      const authError = err as AuthError;
      posthog?.capture("login_failed", {
        method: "apple",
        code: authError.code,
        message: authError.message,
      });
      setError(authError.message || "Failed to sign in with Apple");
    } finally {
      setLoading(false);
    }
  };

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!email || !password) {
      setError("Please fill in all fields");
      return;
    }

    if (mode === "signup" && password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    if (mode === "signup" && !turnstileToken) {
      setError("Please wait for bot verification to complete");
      return;
    }

    setLoading(true);
    try {
      if (mode === "signup") {
        posthog?.capture("sign_up_started", { method: "email" });
        await signUp(email, password);
        posthog?.capture("sign_up_success", { method: "email" });
        resetToken();
        if (redirectToProfileOnSignup) {
          onClose();
          navigate("/profile");
          return;
        }
      } else {
        posthog?.capture("login_started", { method: "email" });
        await signInWithEmail(email, password);
        posthog?.capture("login_success", { method: "email" });
      }
      onSuccess();
    } catch (err) {
      const authError = err as AuthError;
      let message = authError.message || "Authentication failed";
      if (authError.code === "auth/email-already-in-use") {
        message = "Email is already in use";
      } else if (authError.code === "auth/invalid-email") {
        message = "Invalid email address";
      } else if (authError.code === "auth/wrong-password") {
        message = "Incorrect password";
      } else if (authError.code === "auth/user-not-found") {
        message = "No account found with this email";
      }
      if (mode === "signup") {
        posthog?.capture("sign_up_failed", {
          method: "email",
          code: authError.code,
          message: authError.message,
        });
      } else {
        posthog?.capture("login_failed", {
          method: "email",
          code: authError.code,
          message: authError.message,
        });
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop with blur */}
      <div
        className="absolute inset-0"
        onClick={onClose}
        style={{
          backgroundColor: "rgba(2, 2, 16, 0.45)",
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
        }}
      />

      {/* Modal */}
      <div
        className="relative w-full max-w-[420px] p-8 sm:p-10"
        style={{
          backgroundColor: containerNeutralSolidSurface,
          borderRadius: 28,
          boxShadow:
            "0 24px 80px rgba(2, 2, 16, 0.18), 0 0 0 1px rgba(2, 2, 16, 0.04)",
          animation: "scaleIn 0.25s cubic-bezier(0.16, 1, 0.3, 1) forwards",
        }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-5 right-5 w-9 h-9 rounded-full flex items-center justify-center transition-all duration-150 hover:scale-105"
          style={{ backgroundColor: "rgba(2, 2, 16, 0.05)" }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ color: textNeutralMuted }}
          >
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>

        {/* Header */}
        <div className="text-center mb-8">
          <h2
            className="text-2xl sm:text-3xl font-gnarly-semibold tracking-tight"
            style={{ color: textNeutralDefault }}
          >
            {mode === "signin"
              ? t("sign_in") || "Sign in"
              : t("create_account") || "Create account"}
          </h2>
          <p
            className="mt-2 text-sm leading-relaxed"
            style={{ color: textNeutralMuted }}
          >
            {t("auth_subtitle") || "Sign in to create your flat listing"}
          </p>
        </div>

        {/* Google button */}
        <button
          onClick={handleGoogleSignIn}
          disabled={loading}
          className="w-full flex items-center justify-center gap-3 px-5 py-3.5 rounded-2xl font-medium transition-all duration-200 hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50"
          style={{
            backgroundColor: "rgba(2, 2, 16, 0.04)",
            color: textNeutralDefault,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "rgba(2, 2, 16, 0.07)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "rgba(2, 2, 16, 0.04)";
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          {t("continue_with_google") || "Continue with Google"}
        </button>

        {/* Apple button */}
        <button
          onClick={handleAppleSignIn}
          disabled={loading}
          className="w-full flex items-center justify-center gap-3 px-5 py-3.5 rounded-2xl font-medium transition-all duration-200 hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50 mt-3"
          style={{
            backgroundColor: "rgba(2, 2, 16, 0.04)",
            color: textNeutralDefault,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "rgba(2, 2, 16, 0.07)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "rgba(2, 2, 16, 0.04)";
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24">
            <path
              fill="currentColor"
              d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"
            />
          </svg>
          {t("continue_with_apple") || "Continue with Apple"}
        </button>

        {/* Divider */}
        <div className="flex items-center gap-4 my-6">
          <div
            className="flex-1 h-px"
            style={{ backgroundColor: "rgba(2, 2, 16, 0.08)" }}
          />
          <span
            className="text-xs tracking-wider uppercase font-medium"
            style={{ color: textNeutralMuted }}
          >
            {t("or") || "or"}
          </span>
          <div
            className="flex-1 h-px"
            style={{ backgroundColor: "rgba(2, 2, 16, 0.08)" }}
          />
        </div>

        {/* Email form */}
        <form onSubmit={handleEmailAuth} className="space-y-3.5">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("email") || "Email"}
            className="cf-input-boxed"
            style={{ color: textNeutralDefault }}
          />

          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t("password") || "Password"}
            className="cf-input-boxed"
            style={{ color: textNeutralDefault }}
          />

          {mode === "signup" && (
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder={t("confirm_password") || "Confirm password"}
              className="cf-input-boxed"
              style={{ color: textNeutralDefault }}
            />
          )}

          {error && (
            <div
              className="px-4 py-3 rounded-xl text-sm"
              style={{
                backgroundColor: "rgba(220, 54, 46, 0.07)",
                color: "#B02B25",
              }}
            >
              {error}
            </div>
          )}

          {mode === "signup" && (
            <TurnstileWidget
              siteKey={siteKey}
              widgetRef={widgetRef}
              onSuccess={onTurnstileSuccess}
              onExpire={onTurnstileExpire}
              onError={onTurnstileError}
            />
          )}

          <button
            type="submit"
            disabled={loading || (mode === "signup" && !turnstileToken)}
            className="w-full px-5 py-3.5 rounded-2xl font-gnarly-semibold transition-all duration-200 hover:scale-[1.01] active:scale-[0.99] disabled:opacity-40 disabled:hover:scale-100"
            style={{
              backgroundColor: accentColor,
              color: textNeutralOnPrimary,
            }}
          >
            {loading
              ? t("loading") || "Loading..."
              : mode === "signin"
                ? t("sign_in") || "Sign in"
                : t("create_account") || "Create account"}
          </button>
        </form>

        {/* Toggle mode */}
        <p
          className="mt-7 text-center text-sm"
          style={{ color: textNeutralMuted }}
        >
          {mode === "signin" ? (
            <>
              {t("no_account") || "Don't have an account?"}{" "}
              <button
                onClick={() => {
                  setMode("signup");
                  setError("");
                }}
                className="font-medium transition-opacity hover:opacity-70"
                style={{ color: accentColor }}
              >
                {t("sign_up") || "Sign up"}
              </button>
            </>
          ) : (
            <>
              {t("have_account") || "Already have an account?"}{" "}
              <button
                onClick={() => {
                  setMode("signin");
                  setError("");
                }}
                className="font-medium transition-opacity hover:opacity-70"
                style={{ color: accentColor }}
              >
                {t("sign_in") || "Sign in"}
              </button>
            </>
          )}
        </p>
      </div>
    </div>
  );
};

export default AuthModal;
